import { selectorFamily } from "recoil"
import { getInboxContacts } from "src/apis/contact/getInboxContacts"
import { inboxContactsRequestIdState } from "src/recoil/atoms/contact/inboxContactsRequestIdState"
import { ContactOverviewModel } from "src/types/contact/ContactOverviewModel"
import { selectedMyOrganizationSelector } from "src/recoil/selectors/organization/selectedMyOrganizationSelector"
import { tokensState } from "src/recoil/atoms/authorization/tokensState"
import { ContactFilterValue } from "src/types/contact/ContactFilter"
import { CustomAlert } from "src/utils/CustomAlert"
import { myProfileSelectorFamily } from "src/recoil/selectors/organization/myProfile/myProfileSelectorFamily"
import { multiTeamMemberImageUrls } from "src/apis/team/getMemberImageUrl"
import { ContactListCacheService } from "src/services/cache/ContactListCacheService"
import { Platform } from "react-native"

interface TeamMemberImageType {
  isDefault?: boolean
  teamMemberId: string
  url: string
}

export const inboxContactsSelectorFamily = selectorFamily<
  { contacts: ContactOverviewModel[]; nextToken?: string; total?: number | null; pageIndex?: number } | undefined,
  {
    searchText?: string
    filters?: ContactFilterValue[]
    count?: number
    organizationId?: string
    nextToken?: string
    pageIndex?: number
  }
>({
  key: "selectors/contact/inboxContactsSelectorFamily",
  get:
    ({ searchText, filters, count, organizationId, nextToken, pageIndex }) =>
    async ({ get }) => {
      get(inboxContactsRequestIdState)
      const tokens = get(tokensState)
      const accessToken = tokens?.accessToken
      if (accessToken == null) {
        return
      }

      const organizationId_ = organizationId || get(selectedMyOrganizationSelector)?.id
      if (organizationId_ == null) {
        return
      }
      const myProfile = get(myProfileSelectorFamily(organizationId_))
      if (myProfile == null) {
        return
      }

      const currentPageIndex = pageIndex ?? 0

      // ページネーションキャッシュをチェック
      const paginationCache = ContactListCacheService.getPaginationCache({
        teamMemberId: myProfile.memberId,
        containerType: "inbox",
        filters: filters || [],
        searchText,
      })

      // キャッシュにページが存在する場合
      if (paginationCache && paginationCache.pages[currentPageIndex]) {
        const cachedPage = paginationCache.pages[currentPageIndex]

        // キャッシュヒット: URLをそのまま返す（再レンダリングを避けるためタイムスタンプは更新しない）
        // 画像の更新が必要な場合は、Pull-to-Refreshでキャッシュを無効化して新しいデータを取得する
        return {
          contacts: cachedPage.contacts,
          nextToken: cachedPage.nextToken,
          total: paginationCache.total,
          pageIndex: currentPageIndex,
        }
      }

      // 後方互換性: 従来のキャッシュチェック（初回ページのみ）
      if (!nextToken && !pageIndex) {
        const cached = ContactListCacheService.get({
          teamMemberId: myProfile.memberId,
          containerType: "inbox",
          filters: filters || [],
          searchText,
        })

        if (cached) {
          // 従来のキャッシュからページネーションキャッシュに移行
          ContactListCacheService.updatePaginationCachePage(
            {
              teamMemberId: myProfile.memberId,
              containerType: "inbox",
              filters: filters || [],
              searchText,
              pageIndex: 0,
            },
            {
              contacts: cached.contacts,
              nextToken: cached.nextToken,
              total: cached.total,
            }
          )

          // キャッシュヒット: URLをそのまま返す（再レンダリングを避けるためタイムスタンプは更新しない）
          return {
            contacts: cached.contacts,
            nextToken: cached.nextToken,
            total: cached.total,
            pageIndex: 0,
          }
        }
      }

      // キャッシュミスまたはページネーション: API呼び出し
      const result = await getInboxContacts({
        accessToken,
        myMemberId: myProfile.memberId,
        searchText,
        organizationId: organizationId_,
        filters,
        count,
        nextToken,
      })
      if (!result.isOk) {
        // オフライン時のフォールバック: TTL期限切れのキャッシュでも表示する
        // 1. ページネーションキャッシュをチェック
        const expiredPaginationCache = ContactListCacheService.getPaginationCache(
          {
            teamMemberId: myProfile.memberId,
            containerType: "inbox",
            filters: filters || [],
            searchText,
          },
          { ignoreExpiration: true } // TTLを無視して古いキャッシュも取得
        )

        if (expiredPaginationCache && expiredPaginationCache.pages[currentPageIndex]) {
          console.log("[inboxContactsSelectorFamily] Network error, using expired pagination cache as fallback")
          const cachedPage = expiredPaginationCache.pages[currentPageIndex]
          return {
            contacts: cachedPage.contacts,
            nextToken: cachedPage.nextToken,
            total: expiredPaginationCache.total,
            pageIndex: currentPageIndex,
          }
        }

        // 2. 従来のキャッシュをチェック（後方互換性）
        const expiredCache = ContactListCacheService.get(
          {
            teamMemberId: myProfile.memberId,
            containerType: "inbox",
            filters: filters || [],
            searchText,
          },
          { ignoreExpiration: true } // TTLを無視して古いキャッシュも取得
        )

        if (expiredCache) {
          console.log("[inboxContactsSelectorFamily] Network error, using expired cache as fallback")
          return {
            contacts: expiredCache.contacts,
            nextToken: expiredCache.nextToken,
            total: expiredCache.total,
            pageIndex: 0,
          }
        }

        // キャッシュもない場合のみエラー表示
        // Web版では非同期アラートの後にundefinedを返すと画面が真っ白になるため、
        // 空の配列を返してエラーメッセージをログに出力する
        console.error("[inboxContactsSelectorFamily] Network error and no cache available:", result.error.message)
        if (Platform.OS !== "web") {
          CustomAlert.alert("エラー", result.error.message)
        }
        // 空の結果を返す（真っ白を防ぐ）
        return {
          contacts: [],
          nextToken: undefined,
          total: 0,
          pageIndex: currentPageIndex,
        }
      }

      // 送信者の画像URL取得
      const uniqueSenders: any = {}
      const expect: any = []
      result.content.contacts.forEach((item) => {
        if (!uniqueSenders[item.senderId]) {
          uniqueSenders[item.senderId] = true
          expect.push({
            teamMemberId: item.senderId,
            thumbnail: false,
          })
        }
      })
      const dataImages = await multiTeamMemberImageUrls({
        accessToken: accessToken,
        teamMemberImages: expect,
      })
      let teamMemberImage: TeamMemberImageType[] = []
      if (dataImages.isOk) teamMemberImage = dataImages.content

      const accountImageMap: Record<string, string> =
        teamMemberImage?.reduce((map, ele) => {
          const baseUrl = ele.url.split("?")[0]
          return {
            ...map,
            [ele.teamMemberId]: baseUrl,
          }
        }, {}) ?? {}

      const resultAddImageUrl = result.content.contacts.map((item) => {
        return {
          ...item,
          imgUrl: `${accountImageMap[item.senderId ?? ""] ?? "default/account/image/profile.png"}?${new Date().getTime()}`,
        }
      })

      const mixResult = {
        contacts: resultAddImageUrl,
        nextToken: result.content.nextToken,
        total: result.content.total,
        pageIndex: currentPageIndex,
      }

      // ページネーションキャッシュに保存
      ContactListCacheService.updatePaginationCachePage(
        {
          teamMemberId: myProfile.memberId,
          containerType: "inbox",
          filters: filters || [],
          searchText,
          pageIndex: currentPageIndex,
        },
        {
          contacts: resultAddImageUrl,
          nextToken: mixResult.nextToken,
          total: mixResult.total ?? undefined,
        }
      )

      // 後方互換性: 初回ページの場合は従来のキャッシュにも保存
      if (!nextToken && currentPageIndex === 0) {
        ContactListCacheService.set(
          {
            teamMemberId: myProfile.memberId,
            containerType: "inbox",
            filters: filters || [],
            searchText,
          },
          {
            contacts: resultAddImageUrl,
            nextToken: mixResult.nextToken,
            total: mixResult.total ?? undefined,
          }
        )
      }

      return mixResult
    },
})
